import React, { useEffect, useRef, useState } from 'react';
import TabOfDataTable from '../../../Elements/DataTable/TabOfDataTable';
import GeneralEditPanelBase from '../../../Elements/GeneralEditPanelBase';
import ErrorMessage from '../../../Elements/ErrorMessage';
import SurveyTransferMappingService from '../../../Services/SurveyTransferMappingService';
import SurveyTransferAnswerOptionMappingItem from '../../../Domain/SurveyTransferAnswerOptionMappingItem';
import SurveyTransferAnswerOptionRulesSearchParams from './SurveyTransferAnswerOptionRulesSearchParams';
import SurveyTransferAnswerOptionMappingPanelDescription from './SurveyTransferAnswerOptionMappingPanelDescription';
import OrderDirectionsEnum from '../../../Enums/OrderDirectionsEnum';

// Page (reached via ?id=<SurveyTransferQuestionMappingId>) that lists the answer-option rules
// of a single question rule, with server-side filtering/paging. Each row has an Edit action;
// editing (via the edit panel) re-points the source answer option.
const SurveyTransferAnswerOptionRulesDetails = () => {
  const surveyTransferQuestionMappingId = new URLSearchParams(window.location.search).get('id');

  const [searchItem, setSearchItem] = useState<any>(() => {
    // Skip the URL-param loading of SearchParamsBase (the only url param here is ?id=).
    const sp: any = new (SurveyTransferAnswerOptionRulesSearchParams as any)(true);
    sp.SurveyTransferQuestionMappingId = surveyTransferQuestionMappingId;
    return sp;
  });
  // The source answer options are the same for every row (the parent question mapping's source
  // question's options), so fetch them once and reuse as the dropdown options.
  const [sourceAnswerOptionOptions, setSourceAnswerOptionOptions] = useState<any[]>([]);
  const [editedItem, setEditedItem] = useState<any>(null);
  // Header context: the parent question mapping (question texts + third-party question ids) and
  // its parent survey transfer mapping (source/target system + survey names).
  const [questionMapping, setQuestionMapping] = useState<any>(null);
  const [mapping, setMapping] = useState<any>(null);
  // Ref to the table so a save can refresh the CURRENT page (instead of resetting to page 1).
  const tableRef = useRef<any>(null);

  useEffect(() => {
    if (!surveyTransferQuestionMappingId) {
      return;
    }
    SurveyTransferMappingService.GetQuestionMappingById(surveyTransferQuestionMappingId)
      .then((qm: any) => {
        setQuestionMapping(qm);
        if (qm && qm.surveyTransferMappingId) {
          return SurveyTransferMappingService.GetSurveyTransferMappingById(qm.surveyTransferMappingId).then((m: any) =>
            setMapping(m)
          );
        }
        return null;
      })
      .catch((error: any) => ErrorMessage.SetErrorMessage('STAOR05', error));
  }, [surveyTransferQuestionMappingId]);

  useEffect(() => {
    if (!surveyTransferQuestionMappingId) {
      return;
    }
    SurveyTransferMappingService.GetSourceAnswerOptions(surveyTransferQuestionMappingId)
      .then((result: any) =>
        setSourceAnswerOptionOptions(
          (result || []).map((o: any) => ({
            value: o.tpAnswerOptionId,
            text: o.optionText,
            secondaryId: o.optionValue,
          }))
        )
      )
      .catch((error: any) => ErrorMessage.SetErrorMessage('STAOR03', error));
  }, [surveyTransferQuestionMappingId]);

  if (!surveyTransferQuestionMappingId) {
    ErrorMessage.SetErrorMessage('STAOR01');
    return null;
  }

  // Wire the row "Edit" action: attach the (shared) source-answer-option options to the edited
  // item so the panel's SourceQuestionSelect can render them.
  SurveyTransferAnswerOptionMappingItem.editFunction = (id: any, item: any) =>
    setEditedItem({ ...item, SourceAnswerOptionOptions: sourceAnswerOptionOptions });

  // The parent question-mapping id is not a visible search field, and "Clear All" rebuilds
  // the search params from scratch, so re-inject the scope on every search.
  const startSearch = (newSearchItem: any) => {
    setSearchItem({ ...newSearchItem, SurveyTransferQuestionMappingId: surveyTransferQuestionMappingId });
  };

  const closeEditPanel = (refresh: any) => {
    setEditedItem(null);
    // GeneralEditPanelBase calls onClose(true) on a successful save; refresh the current page
    // (the table's refresh keeps the current page, unlike bumping searchItem which resets to 1).
    if (refresh === true && tableRef.current) {
      tableRef.current.refresh();
    }
  };

  return (
    <div className='contentwrapper'>
      {mapping && (
        <div className='row m-1'>
          <div className='col-6'>
            <strong>Target Survey:</strong> {mapping.targetSystemName ? `${mapping.targetSystemName} - ` : ''}
            {mapping.targetTpSurveyName || '-'}
          </div>
          <div className='col-6'>
            <strong>Source Survey:</strong> {mapping.sourceSystemName ? `${mapping.sourceSystemName} - ` : ''}
            {mapping.sourceTpSurveyName || '-'}
          </div>
        </div>
      )}
      {questionMapping && (
        <div className='row m-1'>
          <div className='col-6'>
            <strong>Target Question:</strong> {questionMapping.targetTpQuestionText || '-'}
            {questionMapping.targetThirdPartyQuestionId != null
              ? ` (${questionMapping.targetThirdPartyQuestionId})`
              : ''}
          </div>
          <div className='col-6'>
            <strong>Source Question:</strong> {questionMapping.sourceTpQuestionText || '-'}
            {questionMapping.sourceThirdPartyQuestionId != null
              ? ` (${questionMapping.sourceThirdPartyQuestionId})`
              : ''}
          </div>
        </div>
      )}
      {editedItem && (
        <GeneralEditPanelBase
          popup={true}
          editedItem={editedItem}
          panelDescription={SurveyTransferAnswerOptionMappingPanelDescription}
          onClose={closeEditPanel}
          maxWidth={1000}
          errorCode='STAOR04'
        />
      )}
      <TabOfDataTable
        ref={tableRef}
        type={SurveyTransferAnswerOptionMappingItem}
        serviceFunction={SurveyTransferMappingService.GetAnswerOptionRulesBySearching}
        convertDBItemsFunction={SurveyTransferMappingService.ConvertAnswerOptionRuleDBItems}
        orderBy='TargetTpAnswerOptionDisplayOrder'
        orderDirection={OrderDirectionsEnum.Asc}
        searchItem={searchItem}
        searchParamsType={SurveyTransferAnswerOptionRulesSearchParams}
        showSearchStrip={true}
        clickOnStartSearch={startSearch}
        enableUrlPush={false}
        popupEnabled={false}
        errorCode='STAOR02'
      />
    </div>
  );
};

export default SurveyTransferAnswerOptionRulesDetails;
